import {Localisation} from "@hps/hops-sdk-js";
import {InputAdornment} from "@mui/material";
import React from "react";

import TextField from "./TextField.js";

/**
 * Price input.
 *
 * @package HOPS
 * @subpackage Components
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */
class PriceInput extends React.PureComponent {

	/**
	 * Constructor.
	 *
	 * @param {Object} props
	 * @return {self}
	 */
	constructor(props) {
		super(props);

		/**
		 * State
		 *
		 * @type {Object}
		 */
		this.state = {

			/**
			 * Focused?
			 * 
			 * @type {Boolean}
			 */
			focused: false,

			/**
			 * Initial value
			 *
			 * @type {String|null}
			 */
			value: props.value

		};

		/**
		 * Method binds
		 */
		this.handleBlur = this.handleBlur.bind(this);
		this.handleChange = this.handleChange.bind(this);
		this.handleFocus = this.handleFocus.bind(this);

	}


	/**
	 * Component updated.
	 *
	 * @param {Object} prevProps
	 * @return {void}
	 */
	componentDidUpdate(prevProps) {
		if (prevProps.value !== this.props.value) {
			if (this.props.value !== (this.state.value * 100)) {
				this.setState({value: this.props.value});
			}
		}
	}


	/**
	 * Blurred.
	 *
	 * @return {void}
	 */
	handleBlur() {

		this.setState({focused: false});

		if (this.state.value) {
			const value = this.valueFormatted;
			this.reportChange(value);
			this.setState({value});
		}

		if (this.props.onBlur) this.props.onBlur();

	}


	/**
	 * Value changed.
	 * 
	 * @param {String} value
	 * @return {void}
	 */
	handleChange(value) {
		this.reportChange(value);
		this.setState({value});
	}


	/**
	 * Focused.
	 * 
	 * @return {void}
	 */
	handleFocus() {
		this.setState({focused: true});
	}


	/**
	 * Report a value change.
	 * 
	 * @param {mixed} value
	 * @return {void}
	 */
	reportChange(value) {
		const parsed = Number.parseFloat(value);
		this.props.onChange((parsed ? (parsed * 100) : null));
	}


	/**
	 * Render.
	 * 
	 * @return {ReactNode}
	 */
	render() {
		return (
			<TextField
				autoFocus={this.props.autoFocus}
				color={this.props.color}
				controlled={true}
				disabled={this.props.disabled}
				fullWidth={this.props.fullWidth}
				inputProps={this.constructor.inputProps}
				InputProps={this.constructor.InputProps}
				label={this.props.label}
				onBlur={this.handleBlur}
				onChange={this.handleChange}
				onFocus={this.handleFocus}
				required={this.props.required}
				pattern="^[0-9.]+$"
				placeholder={this.props.placeholder}
				value={this.value} />
		);
	}


	/**
	 * Get `value`.
	 * 
	 * @return {mixed}
	 */
	get value() {
		if (!this.props.value || this.state.focused) return this.state.value;
		else return this.valueFormatted;
	}


	/**
	 * Get `value` formatted as a currency string.
	 * 
	 * @return {String|null}
	 */
	get valueFormatted() {
		if (!this.state.value) return null;
		else return Number.parseFloat(this.state.value).toFixed(2);
	}


	/**
	 * `inputProps`
	 * 
	 * @type {Object}
	 */
	static inputProps = {min: 0};

	/**
	 * `InputProps`
	 * 
	 * @type {Object}
	 */
	static InputProps = {startAdornment: <InputAdornment position="start">{Localisation.getCurrencySymbol()}</InputAdornment>};

}

export default PriceInput;
